// src/components/layout/Header.tsx
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, Lock } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

// Definizione voci menu
const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'about', labelKey: 'nav.about' },
  { key: 'news', labelKey: 'nav.news' },
  { key: 'breweries', labelKey: 'nav.breweries' },
  { key: 'work', labelKey: 'nav.work' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  // Estrai slug corrente
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  // Pagine con hero light che richiedono header solido immediatamente
  const pagesWithLightTop: string[] = []
  const forceSolid = pagesWithLightTop.includes(currentSlug)

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Chiudi mobile menu su cambio route
  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  // Blocca scroll body quando mobile menu aperto
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileOpen])

  const isTransparent = !isScrolled && !forceSolid

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-md'
      )}
    >
      <nav
        aria-label={t('aria.mainNavigation', 'Navigazione principale')}
        className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20"
      >
        {/* Logo */}
        <LocalizedLink to="home" className="flex-shrink-0">
          <img
            src="/images/logo-2048x945-1.png"
            alt="Interbrau"
            width={150}
            height={40}
            className={cn(
              'h-10 md:h-12 w-auto transition-all',
              isTransparent && 'brightness-0 invert'
            )}
          />
        </LocalizedLink>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map(({ key, labelKey }) => {
            const routePath = localizedRoutes[key]
            const isActive =
              routePath.it === currentSlug ||
              routePath.en === currentSlug ||
              (key === 'home' && currentSlug === '')

            return (
              <LocalizedLink
                key={key}
                to={key}
                className={cn(
                  'group relative px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isTransparent
                    ? 'text-white/90 hover:text-white hover:bg-white/10'
                    : 'text-brand-dark hover:text-brand-primary hover:bg-gray-100',
                  isActive && (isTransparent ? 'text-white' : 'text-brand-primary')
                )}
              >
                {t(labelKey)}
                {/* Indicatore attivo */}
                <span
                  className={cn(
                    'absolute bottom-0 left-3 right-3 h-0.5 rounded-full transition-all',
                    isActive
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100',
                    isTransparent ? 'bg-white' : 'bg-brand-primary'
                  )}
                />
              </LocalizedLink>
            )
          })}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-2">
          <LocalizedLink
            to="reservedArea"
            className={cn(
              'p-2 rounded-md transition-colors',
              isTransparent
                ? 'text-white/90 hover:text-white hover:bg-white/10'
                : 'text-brand-dark hover:text-brand-primary hover:bg-gray-100'
            )}
            aria-label={t('reservedArea.title', 'Area Riservata')}
          >
            <Lock className="h-5 w-5" />
          </LocalizedLink>
          <LanguageSelector
            variant="desktop"
            isScrolled={!isTransparent}
          />
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className={cn(
            'lg:hidden p-2 rounded-md transition-colors',
            isTransparent
              ? 'text-white hover:bg-white/10'
              : 'text-brand-dark hover:bg-gray-100'
          )}
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
        >
          {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 top-16 md:top-20 bg-white z-40 overflow-y-auto"
          >
            <nav className="flex flex-col py-4" aria-label={t('aria.mobileNavigation')}>
              {navItems.map(({ key, labelKey }) => {
                const routePath = localizedRoutes[key]
                const isActive =
                  routePath.it === currentSlug ||
                  routePath.en === currentSlug ||
                  (key === 'home' && currentSlug === '')

                return (
                  <LocalizedLink
                    key={key}
                    to={key}
                    className={cn(
                      'px-6 py-3 text-lg font-medium transition-colors',
                      isActive
                        ? 'text-brand-primary bg-brand-primary/10 border-l-4 border-brand-primary'
                        : 'text-brand-dark hover:bg-gray-50'
                    )}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                )
              })}
              <LocalizedLink
                to="reservedArea"
                className={cn(
                  'px-6 py-3 text-lg font-medium transition-colors flex items-center gap-2',
                  currentSlug === 'area-riservata' || currentSlug === 'reserved-area'
                    ? 'text-brand-primary bg-brand-primary/10 border-l-4 border-brand-primary'
                    : 'text-brand-dark hover:bg-gray-50'
                )}
              >
                <Lock className="h-5 w-5" />
                {t('reservedArea.title', 'Area Riservata')}
              </LocalizedLink>
              <div className="border-t border-gray-200 mt-4 pt-4 px-6">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
